import React, { useState } from "react"
import { asDialogData, getDateInServerTimeZone } from "../../../core/utils/utils"
import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, FormControlLabel, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import PaymentsIcon from "@mui/icons-material/Payments"
import { format } from "date-fns"
import {
    useGetAllSeasonsQuery,
    useCreateSeasonMutation,
    useActivateSeasonMutation,
    usePayoutSeasonMutation,
    useLazyGetSeasonActivationPreviewQuery,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"

function formatDate(s) {
    if (!s) return "—"
    try { return format(getDateInServerTimeZone(s), "dd.MM.yyyy HH:mm") } catch { return s }
}

// Сезоны Belka English Pass. Активен ровно один; «Activate» переключает
// счётчики XP и весь сезонный контент (категории, уровни, белки, щиты) на
// целевой сезон и автоматически выплачивает незатребованные награды
// закрываемого сезона. Данные прошлых сезонов не изменяются — это архив.
// «Payout» на закрытом сезоне добирает упавшие выплаты (идемпотентно).
export default function SeasonsAdmin() {
    const { data: seasons = [], isLoading } = useGetAllSeasonsQuery()
    const [createSeason, createResp] = useCreateSeasonMutation()
    const [activateSeason, activateResp] = useActivateSeasonMutation()
    const [payoutSeason, payoutResp] = usePayoutSeasonMutation()
    const [loadPreview, previewResp] = useLazyGetSeasonActivationPreviewQuery()

    const [creating, setCreating] = useState(false)
    const [title, setTitle] = useState("")
    const [confirming, setConfirming] = useState(null) // сезон, ожидающий подтверждения активации
    const [payoutPending, setPayoutPending] = useState(true) // чекбокс «начислить unclaimed reward»
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    async function submitCreate() {
        const resp = await createSeason({ title: title.trim() })
        setActionResp(asDialogData(resp)); setRespOpen(true)
        if (!resp.error) { setCreating(false); setTitle("") }
    }

    function openConfirm(row) {
        setConfirming(row)
        setPayoutPending(true)
        loadPreview(row.number)
    }

    async function submitActivate() {
        const withPayout = payoutPending
        const resp = await activateSeason({ number: confirming.number, payoutPending: withPayout })
        setConfirming(null)
        if (resp.data) {
            const d = resp.data
            setActionResp(asDialogData({
                data: {
                    message: `Season ${d.activatedNumber} activated.`
                        + (withPayout
                            ? ` Rewards paid: ${d.paidRewardBelcoins} BC to ${d.paidRewardUsers} employees.`
                                + (d.payoutErrors ? ` Payout errors: ${d.payoutErrors} — use «Payout» on the closed season to retry.` : "")
                            : " Unclaimed rewards were NOT paid — use «Payout» on the closed season when needed."),
                },
            }))
        } else {
            setActionResp(asDialogData(resp))
        }
        setRespOpen(true)
    }

    async function submitPayout(row) {
        if (!window.confirm(`Retry pending reward payouts for «${row.title}»? Already paid levels are never paid twice.`)) return
        const resp = await payoutSeason(row.number)
        if (resp.data) {
            const d = resp.data
            setActionResp(asDialogData({
                data: {
                    message: `Season ${d.seasonNumber} payout: ${d.paidRewardBelcoins} BC to ${d.paidRewardUsers} employees.`
                        + (d.payoutErrors ? ` Errors: ${d.payoutErrors} (see Sentry).` : "")
                        + (d.skippedFired ? ` Skipped fired: ${d.skippedFired}.` : ""),
                },
            }))
        } else {
            setActionResp(asDialogData(resp))
        }
        setRespOpen(true)
    }

    const columns = [
        { field: "number", headerName: "#", width: 70 },
        { field: "title", headerName: "Title", flex: 1 },
        { field: "active", headerName: "Status", width: 120, renderCell: (p) =>
            p.value
                ? <Chip size="small" color="success" label="active" />
                : <Chip size="small" label="inactive" />
        },
        { field: "startedAt", headerName: "Started", width: 150, renderCell: (p) => formatDate(p.value) },
        { field: "createdAt", headerName: "Created", width: 150, renderCell: (p) => formatDate(p.value) },
        { field: "actions", headerName: "", width: 230, renderCell: (p) =>
            !p.row.active && (
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" color="warning" startIcon={<PlayArrowIcon />}
                            onClick={() => openConfirm(p.row)}>
                        Activate
                    </Button>
                    {/* Добор упавших выплат уже закрытого сезона */}
                    {p.row.startedAt && (
                        <Button size="small" variant="outlined" startIcon={<PaymentsIcon />}
                                disabled={payoutResp.isLoading}
                                onClick={() => submitPayout(p.row)}>
                            Payout
                        </Button>
                    )}
                </Stack>
            )
        },
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    // Превью валидно только для сезона из открытого диалога — иначе при
    // повторном открытии на другом сезоне показались бы устаревшие цифры.
    const preview = previewResp.data && confirming && previewResp.data.targetNumber === confirming.number
        ? previewResp.data
        : null

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Seasons</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                    New season
                </Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                Exactly one season is active. Activation: the active flag flips first, then unclaimed rewards of the
                closed season are auto-paid (fired employees skipped). Past seasons' data is never modified — it is
                the archive shown in «Результаты сезонов». Prepare next-season content (categories, levels, mascots,
                shields) in advance via the Season field in the Configs pages.
            </Alert>
            <Paper sx={{ height: 420 }}>
                <DataGrid
                    rows={seasons}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25]}
                    disableSelectionOnClick
                />
            </Paper>

            <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
                <DialogTitle>New season</DialogTitle>
                <DialogContent>
                    <TextField
                        sx={{ mt: 1 }}
                        fullWidth
                        label="Title"
                        placeholder={`Сезон ${(seasons.length ? Math.max(...seasons.map(s => s.number)) : 0) + 1}`}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreating(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={submitCreate}
                            disabled={!title.trim() || createResp.isLoading}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirming} onClose={() => setConfirming(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Activate «{confirming?.title}»?</DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        <p>Users will immediately see season {confirming?.number} content; XP accumulation restarts
                            from zero and the previous season's results are frozen as archive.</p>
                        {previewResp.isFetching && <CircularProgress size={20} />}
                        {!previewResp.isFetching && previewResp.error && (
                            <Alert severity="error" sx={{ my: 1 }}>
                                Failed to load the activation preview
                                {previewResp.error?.data?.error ? `: ${previewResp.error.data.error}` : ""}.
                                Activation is blocked — retry from the seasons list.
                            </Alert>
                        )}
                        {!previewResp.isFetching && preview && (
                            <Box component="ul" sx={{ pl: 2.5 }}>
                                <li>Unclaimed rewards: <b>{preview.pendingRewardBelcoins} BC</b> held by {preview.usersWithPendingRewards} employees{payoutPending ? " — will be paid out during activation." : " — will stay unpaid (use «Payout» on the closed season later)."}</li>
                                {preview.waitingRequests > 0 && (
                                    <li><b>{preview.waitingRequests} WAITING request(s)</b> reference old-season categories; process them before activation, or pick a new-season category on approve.</li>
                                )}
                                {preview.affectedCoupons > 0 && (
                                    <li><b>{preview.affectedCoupons} valid coupon(s)</b> are affected: old-season-category coupons stop working, uncategorized ones keep accruing — review them.</li>
                                )}
                            </Box>
                        )}
                        <p>Re-running the activation is safe: paid rewards are never duplicated.</p>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={payoutPending}
                                    onChange={e => setPayoutPending(e.target.checked)}
                                />
                            }
                            label="Начислить unclaimed reward"
                        />
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)}>Cancel</Button>
                    {/* Активация заблокирована, пока превью не загружено: админ
                        не должен подтверждать финансовую операцию вслепую. */}
                    <Button variant="contained" color="warning" onClick={submitActivate}
                            disabled={activateResp.isLoading || previewResp.isFetching || !preview}>
                        Activate
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Season"
                information={actionResp?.data?.message || "Saved"}
            />
        </Box>
    )
}
